import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import { useCommonFetch } from "../../../../hooks";
import { getLeaveSummary } from "../../../../services/workspace/leave.service";
import { getUserList } from "../../../../services/workspace/user.service";
import { convertToLabelValue } from "../../../../utils/common";
import DefaultSearchSelectInput from "../../../components/inputs/defaultSearchSelectInput";
import toast from "react-hot-toast";
import moment from "moment";
import { HiChevronDown, HiChevronRight, HiCheckCircle, HiXCircle, HiClock } from "react-icons/hi";

export const items = [{ label: "Leave Summary", path: "" }];

const LeaveSummary = () => {
  const user = useSelector((state) => state.user.user);
  const userType = user?.type;
  const isAdmin = userType === 0 || userType === 1; // Super Admin or Workspace Admin
  
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const fetchTimeoutRef = useRef(null);
  const prevSelectedStaffRef = useRef(null);

  // Toggle row expansion
  const toggleRow = (leaveTypeId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(leaveTypeId)) {
        newSet.delete(leaveTypeId);
      } else {
        newSet.add(leaveTypeId);
      }
      return newSet;
    });
  };

  // Fetch staff list for admin
  useEffect(() => {
    if (isAdmin) {
      setStaffLoading(true);
      getUserList({})
        .then((response) => {
          const users = response.user?.data || [];
          const staffOptions = convertToLabelValue(users);
          setStaffList(staffOptions);
          setStaffLoading(false);
        })
        .catch((error) => {
          toast.error("Failed to fetch staff list!");
          console.error(error);
          setStaffLoading(false);
        });
    }
  }, [isAdmin]);

  // Fetch data with useCommonFetch
  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    handleSearchChange,
    handlePageChange,
    sortingUIProps,
    fetchData,
  } = useCommonFetch(getLeaveSummary, {
    dataPath: "leaveSummary",
    totalPath: "leaveSummary.length",
    errorMessage: "Failed to fetch leave summary data!",
    debounceDelay: 500,
    autoFetch: !isAdmin || (isAdmin && selectedStaff),
    customParams: isAdmin && selectedStaff 
      ? { user_id: selectedStaff.value, with: "leaves.days" } 
      : { with: "leaves.days" },
    sortingOptions: [
      {
        label: "Name",
        field: "name",
        buttonText: { ascending: "A-Z", descending: "Z-A" },
      },
    ],
    sortingModule: "leaveSummary",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingShowDisableToggle: false,
    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        return {
          data: data,
          action: {},
        };
      }
      return { data: [], action: {} };
    },
  });

  // Refetch data when selectedStaff changes (for admin)
  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    if (isAdmin && selectedStaff && fetchData) {
      const currentStaffId = selectedStaff.value;
      const prevStaffId = prevSelectedStaffRef.current;
      
      if (currentStaffId !== prevStaffId) {
        prevSelectedStaffRef.current = currentStaffId;
        fetchTimeoutRef.current = setTimeout(() => {
          fetchData({ user_id: currentStaffId }, true);
        }, 300);
      }
    } else if (isAdmin && !selectedStaff) {
      prevSelectedStaffRef.current = null;
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [selectedStaff, isAdmin, fetchData]);

  // Get status badge
  const getStatusBadge = (status) => {
    if (status === 1) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <HiCheckCircle className="w-3 h-3" />
          Approved
        </span>
      );
    } else if (status === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <HiClock className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <HiXCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  };

  // Get day type badge
  const getDayTypeBadge = (type, isPaid) => {
    const dayType = parseFloat(type);
    if (dayType === 1.0) {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Full Day {isPaid ? "(Paid)" : "(Unpaid)"}
        </span>
      );
    } else if (dayType === 0.5) {
      return (
        <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
          Half Day {isPaid ? "(Paid)" : "(Unpaid)"}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
        {type} Day {isPaid ? "(Paid)" : "(Unpaid)"}
      </span>
    );
  };

  const leaveSummaryData = tableData?.data || [];

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        shorting
        breadcrumbs={items}
        sortingModule="leaveSummary"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
      />

      {/* Staff Selection for Admin */}
      {isAdmin && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex flex-col gap-2">
            <label className="text-gray-700 font-medium text-sm">
              Select Staff <span className="text-red-600">*</span>
            </label>
            <DefaultSearchSelectInput
              options={staffList}
              value={selectedStaff}
              onChange={(selected) => {
                setSelectedStaff(selected);
              }}
              placeholder="Search and select staff member"
              isDisabled={staffLoading}
              className="w-full"
            />
            {!selectedStaff && (
              <p className="text-sm text-gray-500">
                Please select a staff member to view their leave summary
              </p>
            )}
          </div>
        </div>
      )}

      {/* Leave Summary Cards */}
      {!loading && leaveSummaryData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {leaveSummaryData.map((leaveType) => {
            const consumed = parseFloat(leaveType.consumed_leaves_sum || 0);
            const allotted = parseFloat(leaveType.allotted || 0);
            const remaining = parseFloat(leaveType.remaining_leave || 0);
            const carryForward = parseFloat(leaveType.carry_forward || 0);
            const totalAvailable = allotted + carryForward;
            const usagePercentage = totalAvailable > 0 ? (consumed / totalAvailable) * 100 : 0;

            return (
              <div
                key={leaveType.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{leaveType.name}</h3>
                    <p className="text-xs text-gray-500">{leaveType.code}</p>
                  </div>
                  {leaveType.status === 1 ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Allotted:</span>
                    <span className="font-semibold text-gray-900">{allotted}</span>
                  </div>
                  {carryForward > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Carry Forward:</span>
                      <span className="font-semibold text-blue-600">+{carryForward}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Consumed:</span>
                    <span className="font-semibold text-orange-600">{consumed}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Remaining:</span>
                    <span className="font-bold text-lg text-green-600">{remaining}</span>
                  </div>
                  
                  {/* Usage Progress Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Usage</span>
                      <span>{usagePercentage.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          usagePercentage > 80
                            ? "bg-red-500"
                            : usagePercentage > 50
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                        style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detailed Leave Summary Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {isAdmin && !selectedStaff ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 text-sm">
              Please select a staff member to view their leave summary
            </p>
          </div>
        ) : loading ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 text-sm">Loading leave summary...</p>
          </div>
        ) : leaveSummaryData.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 text-sm">No leave data available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Leave Type
                  </th>
   
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Allotted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Consumed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Remaining
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Applications
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaveSummaryData.map((leaveType) => {
                  const isExpanded = expandedRows.has(leaveType.id);
                  const leaves = leaveType.leaves || [];
                  const hasLeaves = leaves.length > 0;

                  return (
                    <React.Fragment key={leaveType.id}>
                      <tr
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                          isExpanded ? "bg-blue-50" : ""
                        }`}
                        onClick={() => hasLeaves && toggleRow(leaveType.id)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          {hasLeaves ? (
                            isExpanded ? (
                              <HiChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <HiChevronRight className="w-5 h-5 text-gray-500" />
                            )
                          ) : (
                            <span className="w-5 h-5 block"></span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="font-medium text-gray-900">{leaveType.name}</div>
                            <div className="text-sm text-gray-500">{leaveType.abrv}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {leaveType.allotted || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-600 font-medium">
                          {leaveType.consumed_leaves_sum || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                          {leaveType.remaining_leave || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {leaves.length} {leaves.length === 1 ? "application" : "applications"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {leaveType.status === 1 ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Inactive
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Leave Applications */}
                      {isExpanded && hasLeaves && (
                        <tr>
                          <td colSpan="8" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              {leaves.map((leave) => (
                                <div
                                  key={leave.id}
                                  className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div>
                                      <div className="flex items-center gap-3 mb-2">
                                        <h4 className="font-semibold text-gray-900">
                                          Leave Application #{leave.id}
                                        </h4>
                                        {getStatusBadge(leave.status)}
                                      </div>
                                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                        <div>
                                          <span className="font-medium">Period:</span>{" "}
                                          {moment(leave.start_date).format("MMM DD, YYYY")} -{" "}
                                          {moment(leave.end_date).format("MMM DD, YYYY")}
                                        </div>
                                        <div>
                                          <span className="font-medium">Total Days:</span>{" "}
                                          <span className="font-semibold text-gray-900">
                                            {leave.total_days}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {leave.reason && (
                                    <div className="mb-3">
                                      <p className="text-sm">
                                        <span className="font-medium text-gray-700">Reason:</span>{" "}
                                        <span className="text-gray-600">{leave.reason}</span>
                                      </p>
                                    </div>
                                  )}

                                  {leave.remarks && (
                                    <div className="mb-3">
                                      <p className="text-sm">
                                        <span className="font-medium text-gray-700">Remarks:</span>{" "}
                                        <span className="text-gray-600">{leave.remarks}</span>
                                      </p>
                                    </div>
                                  )}

                                  {/* Leave Days Breakdown */}
                                  {leave.days && leave.days.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                      <h5 className="font-medium text-gray-700 mb-2 text-sm">
                                        Leave Days Breakdown:
                                      </h5>
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {leave.days.map((day) => (
                                          <div
                                            key={day.id}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                                          >
                                            <div className="flex-1">
                                              <div className="text-sm font-medium text-gray-900">
                                                {moment(day.date).format("MMM DD, YYYY")}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {moment(day.date).format("dddd")}
                                              </div>
                                            </div>
                                            <div className="ml-2">
                                              {getDayTypeBadge(day.type, day.is_paid)}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                                    <div className="flex flex-wrap gap-4">
                                      <span>
                                        Created: {moment(leave.created_at).format("MMM DD, YYYY")}
                                      </span>
                                      {leave.updated_at && (
                                        <span>
                                          Updated: {moment(leave.updated_at).format("MMM DD, YYYY")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveSummary;
